'use client';

import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal from '@ebay/nice-modal-react';
import {
  ArrowLeftIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  MicrophoneIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/solid';
import {
  Button, ChatTypingDots,
} from '@components/common';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import { Message, useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import ChatTalentModal from '@components/templates/homepage/chat/ChatTalentModal';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { showVoiceAgentModalWithAuth } from '@utils/voice-agent/showVoiceAgentModalWithAuth';
import { v4 as uuidv4 } from 'uuid';

interface MissionControlBestieChatProps {
  locale: Locale;
  promptRequest: {
    id: string;
    text: string;
  } | null;
  /** When provided, a Back button is rendered. Omit for the default full-page view. */
  onClose?: () => void;
}

const agent = {
  id: '',
};

export const MissionControlBestieChat = (props: MissionControlBestieChatProps) => {
  const {
    locale,
    promptRequest,
    onClose,
  } = props;
  const { t } = useTranslation('common');
  const {
    stream, startStream,
  } = useStreamStateV2(null);
  const anonSessionId = useAnonSessionId();
  const handledPromptIdRef = useRef<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);
  const [expandState, setExpandState] = useState<{ showToggle: boolean; isExpanded: boolean; toggle:() => void } | null>(null);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId ?? '',
  }), [anonSessionId]);

  const composer = useChatComposer({
    history: stream?.messages,
    onSubmit: async (text) => {
      const message = { id: uuidv4(), content: text, type: 'human' } as const;
      await startStream(message, agent.id || '', {
        headers,
        configurations: { user_locale: locale || 'ro' },
      });
    },
  });

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }, [stream?.messages, stream?.status]);

  useEffect(() => {
    if (!promptRequest || !anonSessionId) return;
    if (handledPromptIdRef.current === promptRequest.id) return;

    handledPromptIdRef.current = promptRequest.id;

    const message = {
      id: uuidv4(),
      content: promptRequest.text,
      type: 'human',
    } as const;

    void startStream(message, agent.id || '', {
      headers,
      configurations: { user_locale: locale || 'ro' },
    });
  }, [promptRequest, anonSessionId, startStream, headers, locale]);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items
      && item?.response_metadata?.customData?.jobSearchResults?.items?.length > 0))
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];

    // Use a Map to remove duplicates based on message id
    const ordered = [...new Map(stream.messages.map((message) => [message.id, message])).values()];

    // In flex-col (forward) layout, tool results arrive before the AI text in streaming,
    // so results end up visually above the text. Swap consecutive [results, text-only AI]
    // pairs so text appears above results in the UI.
    for (let i = 0; i < ordered.length - 1; i += 1) {
      const curr = ordered[i];
      const next = ordered[i + 1];
      const currHasResults = (curr?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (curr?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      const nextHasResults = (next?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (next?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      if (currHasResults && !nextHasResults && next?.type === 'ai') {
        ordered[i] = next;
        ordered[i + 1] = curr;
      }
    }

    return ordered.map((message, index) => (
      <div key={`message-container-${message.id}`} className="flex flex-col gap-4">
        <ChatMessageComponent
          key={`message-${message.id}`}
          message={{ ...message }}
          ref={index === ordered.length - 1 ? lastMessageRef : null}
          locale={locale}
          onInteraction={onCvDetailOpen}
        />
      </div>
    ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);
  return (
    <div className="flex min-h-0 flex-1 flex-col bg-surface-100">
      <div className="container mx-auto flex min-h-0 flex-1 flex-col px-4 py-6">

        {/* Overlay header — only shown when opened on top of a job list (onClose provided) */}
        {onClose && (
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-ink">{t('mc.career.coaching')}</h1>
              <p className="text-sm text-ink-light">{t('homepage.hero.subtitle')}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-full border border-input bg-surface px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-surface-200"
            >
              <ArrowLeftIcon className="size-4" />
              {t('global.label.back', 'Back')}
            </button>
          </div>
        )}

        <div
          ref={messagesContainerRef}
          className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pb-6"
        >
          {messagesList}
          {stream?.status === 'inflight' && <ChatTypingDots />}
        </div>
      </div>

      <div className="sticky bottom-0 z-10 border-t border-input bg-surface-100/95 backdrop-blur">
        <div className="container mx-auto px-4 pb-4 pt-3">
          <form
            onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
            className="mx-auto w-full max-w-3xl"
          >
            <div className="mx-auto flex w-full max-w-3xl items-end gap-2 rounded-[24px] border border-input bg-surface px-4 py-3 shadow-lg backdrop-blur-sm">
              <ChatTextarea
                required={false}
                textareaRef={composer.textareaRef}
                input={composer.input}
                handleInputChange={composer.onChange}
                handleKeyDown={composer.onKeyDown}
                placeholder={t('homepage.chat.input.placeholder')}
                customWrapperStyle="flex-1 rounded-2xl"
                customTextareaStyle="transition-none bg-transparent text-ink placeholder:text-ink-light placeholder:opacity-100 placeholder:text-base px-2 text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-normal resize-none cursor-text pointer-events-auto placeholder:truncate placeholder:overflow-ellipsis leading-normal py-1.5 break-words"
                textareaSizeStyle="w-full min-h-10"
                textareaWrapperSizeStyle="w-full min-h-10"
                textareaAlignmentStyle="px-2 py-1.5"
                textareaWrapperAlignmentStyle="m-0"
                collapsedLineLimit={6}
                expandedLineLimit={15}
                onExpandStateChange={setExpandState}
              />

              <div className="flex shrink-0 flex-col items-center self-stretch">
                {expandState?.showToggle && (
                  <button
                    type="button"
                    onClick={expandState.toggle}
                    className="mt-1 shrink-0 rounded p-1 text-ink transition-colors hover:text-ink"
                    aria-label={expandState.isExpanded ? 'Collapse' : 'Expand'}
                  >
                    {expandState.isExpanded
                      ? <ArrowsPointingInIcon className="size-5" />
                      : <ArrowsPointingOutIcon className="size-5" />}
                  </button>
                )}
                <Button
                  type="button"
                  styling="outline"
                  color="light"
                  rounding="none"
                  className="mt-auto !h-11 !w-11 shrink-0 !rounded-xl !p-0"
                  onClick={() => {
                    if (!composer.input.trim()) {
                      sendGoogleTagEvent('bestie_conversations_banner_call', 'Custom');
                      showVoiceAgentModalWithAuth(agent.id, locale);
                    } else {
                      void composer.submit();
                    }
                  }}
                >
                  {!composer.input.trim() ? (
                    <MicrophoneIcon className="size-5" />
                  ) : (
                    <PaperAirplaneIcon className="size-5" />
                  )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>

      <ChatJobModal locale={locale} jobsArray={jobsArray} />
      <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
    </div>
  );
};
